import { useEffect, useState } from "react";
import { PiSquare, PiCheckSquare, PiSquareDuotone } from "react-icons/pi";
import { useNavigate } from "react-router-dom";
import { IInferenceFactorListType } from "../../features/components/feedback/types/feedback_type";
import { FeedbackAccordionList } from "../../features/components/feedback/ui_elements/FeedbackAccordionList";
import { FeeedbackSaveButton } from "../../features/components/feedback/ui_elements/FeedbackSaveButton";
import { FeeedbackTempSaveButton } from "../../features/components/feedback/ui_elements/FeedbackTempSaveButton";
import { GeneralCommonHeader } from "../common/GeneralCommonHeader";
import {
  DISPLAY_HEADER_ICON_BACK,
  DISPLAY_PAGE_FEEDBACK,
} from "../../constants/common_constants";
import { calcUtcDatetime } from "../../utils/basis_util";
import { useDtsAppContext } from "../provider/DtsAppContextProvider";
import { useApi, useErrorDialog, useInitUsageLog } from "../../hooks";
import { getFbInfo } from "../../features/api/dts_api_wrapper";
import { useTranslation } from "react-i18next";
import { FILTER_ITEM, FILTER_CRITERIA } from "../../constants/common_constants";

export const FeedBack = () => {
  // ページ遷移用
  const navigate = useNavigate();
  //言語切替用
  const { t } = useTranslation();

  const {
    dacSubjectName,
    dacModel,
    dacMachineNumber,
    dacInferenceFactor,
    dacSearchHistoryId,
  } = useDtsAppContext();
  const userId =
    localStorage.getItem(
      `CognitoIdentityServiceProvider.${process.env.REACT_APP_AWS_USER_POOLS_WEB_CLIENT_ID}.LastAuthUser`,
    ) || "";
  const searchHistoryId =
    dacSearchHistoryId || localStorage.getItem("searchHistoryId") || "";
  const displayPage = DISPLAY_PAGE_FEEDBACK;
  const displayDateTime = calcUtcDatetime(); //現在日時(UTC時刻)
  // ローカルストレージから取得したデータを変換して格納する変数
  const [listData, setListData] = useState<IInferenceFactorListType>({
    inferenceFactor: [],
  });
  //チェックされた解消要因のラベルリスト
  const [checkResolvedFactorList, setCheckResolvedFactorList] = useState<
    string[]
  >([]);
  const [subjectName, setSubjectName] = useState<string>("");
  const [model, setModel] = useState<string>("");
  const [machineNumber, setMachineNumber] = useState<string>("");

  const { handleApi } = useApi();

  // 利用ログ登録
  const { handleInit: initUsageLog } = useInitUsageLog({
    userId,
    displayPage,
    displayDateTime,
  });

  // エラーダイアログ
  const { showErrorDialog } = useErrorDialog();

  useEffect(() => {
    handleFeedbacklInit();
    const handleStorageChange = (event: StorageEvent) => {
      if (
        event.key ===
        `CognitoIdentityServiceProvider.${process.env.REACT_APP_AWS_USER_POOLS_WEB_CLIENT_ID}.LastAuthUser`
      ) {
        const authUser = localStorage.getItem(
          `CognitoIdentityServiceProvider.${process.env.REACT_APP_AWS_USER_POOLS_WEB_CLIENT_ID}.LastAuthUser`,
        );
        if (!authUser) {
          console.log(`[${displayPage}]err: userId is unknown`);
          showErrorDialog(
            t("共通エラーポップ.エラー"),
            t("共通エラーポップ.通信エラーが発生しました。"),
            "navigateToTopPage",
          ); //エラー", "通信エラーが発生しました。(トップページに遷移する)
        }
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //ページ表示時に行うFeedbackの処理
  const handleFeedbacklInit = async () => {
    try {
      console.log("handleFeedbacklInit start.");
      if (!userId) {
        //何らかの理由でuserIdが読み込めなかった
        throw new Error("userId is unknown");
      }
      if (!searchHistoryId) {
        //何らかの理由でsearchHistoryIdが読み込めなかった
        throw new Error("searchHistoryId is unknown");
      }
      // 初期状態としてコンテキストorローカルストレージからデータを設定し、戻り値として推定要因項目を取得する
      const infData = getInitialFeedbackInfo();
      //利用ログ登録
      await initUsageLog();
      //推定要因項目画面のフィルタ項目をクリア
      localStorage.removeItem(FILTER_ITEM);
      localStorage.removeItem(FILTER_CRITERIA);
      //フィードバック情報取得
      const getFbResult = await handleApi(getFbInfo, userId, searchHistoryId);

      //フィードバック情報取得のエラー処理
      if ("errorMessage" in getFbResult) {
        console.log("errorMessage: ", getFbResult.errorMessage);
        throw new Error(getFbResult.errorMessage);
      }
      //チェック状態の反映
      configureInitialFbChecked(getFbResult, infData);
    } catch (err) {
      console.log(`[${displayPage}]err: `, err);
      if (
        err instanceof Error &&
        err.message === "inferenceFactor is unknown"
      ) {
        //要因取得失敗
        showErrorDialog(
          t("共通エラーポップ.エラー"),
          t("フィードバック画面.要因が見つかりません。"),
          "navigateToTopPage",
        ); //エラー", "要因が見つかりません。"(トップページに遷移する)
      } else if (
        err instanceof Error &&
        (err.message === "Expired token" || err.message === "Invalid token")
      ) {
        //LocalStorageのセッション情報クリア
        const userpool_client_id =
          process.env.REACT_APP_AWS_USER_POOLS_WEB_CLIENT_ID ?? "";
        localStorage.removeItem(
          `CognitoIdentityServiceProvider.${userpool_client_id}.accessToken`
        );
        localStorage.removeItem(
          `CognitoIdentityServiceProvider.${userpool_client_id}.refreshToken`
        );
        localStorage.removeItem(
          `CognitoIdentityServiceProvider.${userpool_client_id}.idToken`
        );
        localStorage.removeItem(
          `CognitoIdentityServiceProvider.${userpool_client_id}.clockDrift`
        );
        localStorage.removeItem(
          `CognitoIdentityServiceProvider.${userpool_client_id}.LastAuthUser`
        );

        showErrorDialog(
          t("共通エラーポップ.エラー"),
          t("共通エラーポップ.セッションがタイムアウトしました。"),
          "ydsyos"
        ); //エラー", "セッションがタイムアウトしました。(YDS/YOSに遷移する)
      } else {
        showErrorDialog(
          t("共通エラーポップ.エラー"),
          t("共通エラーポップ.通信エラーが発生しました。"),
          "navigateToTopPage",
        ); //エラー", "通信エラーが発生しました。(トップページに遷移する)
      }
    }
  };

  //フィードバック取得で取得したチェック状態を反映する
  const configureInitialFbChecked = (getFbResult: any, infData: any) => {
    try {
      //フィードバック情報取得に成功した場合、stateに反映させる
      //直った、直らないの選択状態を反映
      setIsBoxChecked(() => ({
        isSelected: getFbResult.resolved !== "", //未選択じゃなければ選択状態にする
        isFixed: getFbResult.resolved === "0" ? true : false, //"0":直った、"1":直らない(未選択状態のときも同様)
      }));
      //解消要因のラベルを反映
      setCheckResolvedFactorList(getFbResult.resolvedFactor);
      //解消要因チェックボックスの状態を反映
      if (getFbResult.resolvedFactor && getFbResult.resolvedFactor.length > 0) {
        let checkedList = [] as boolean[];
        if (infData) {
          checkedList = infData.inferenceFactor.map(() => false);
          // eslint-disable-next-line array-callback-return
          getFbResult.resolvedFactor.map((item: string) => {
            const index = infData.inferenceFactor.findIndex(
              (data: any) => item === data.issueFactor.label,
            );
            console.log("debug: ", index);
            if (!(index < 0)) {
              checkedList[index] = true;
            }
          });
          console.log(checkedList);
          setIsFactorsChecked(checkedList);
        }
      }
      //上記項目に該当なしのチェックボックス反映
      setIsNotApplicable(
        // 該当項目なしの場合にのみtrue
        getFbResult.notApplicableFlag === "0" ? true : false,
      );
    } catch (err) {
      throw err;
    }
  };

  //Context、ローカルストレージから必要な情報を読み出し、推定要因項目を返す
  const getInitialFeedbackInfo = () => {
    try {
      //案件名、型式、機番、推定要因項目をContext or ローカルストレージから読み出す
      const _subjectName =
        dacSubjectName || localStorage.getItem("subjectName") || "";
      const _model = dacModel || localStorage.getItem("model") || "";
      const _machineNumber =
        dacMachineNumber || localStorage.getItem("machineNumber") || "";
      const infData =
        dacInferenceFactor || localStorage.getItem("inferenceFactor") || "";

      //案件名
      if (_subjectName) {
        setSubjectName(_subjectName);
      } else {
        console.log("subjectName is unknown");
      }

      //型式
      if (_model) {
        setModel(_model);
      } else {
        console.log("model is unknown");
      }

      //機番
      setMachineNumber(_machineNumber);

      //要因以外のパラメーター不足(Context、LocalStorageのどちらからも)取得失敗
      // 機番は読み込めなくても良い
      if (!_subjectName || !_model) {
        throw new Error("failed to load parameter.");
      }

      //要因
      if (infData) {
        setListData(JSON.parse(infData));
        return JSON.parse(infData);
      } else {
        //要因(Context、LocalStorageのどちらからも)取得失敗
        console.log("inferenceFactor is unknown");
        throw new Error("inferenceFactor is unknown");
      }
    } catch (err) {
      throw err;
    }
  };

  // 上部チェックボックス(直った、直らない)の状態を管理
  const [isBoxChecked, setIsBoxChecked] = useState({
    isSelected: false,
    isFixed: false,
  });

  // 要因項目の選択状態の管理
  const [isFactorsChecked, setIsFactorsChecked] = useState<boolean[]>([false]);
  useEffect(() => {
    setIsFactorsChecked(listData.inferenceFactor.map(() => false));
  }, [listData]);

  // 該当項目がない状態の管理
  const [isNotApplicable, setIsNotApplicable] = useState(false);

  // 上部チェックボックスの選択処理
  const handleFixCheck = (bool: boolean) => {
    if (isBoxChecked.isFixed !== bool) {
      // 選択されているボックスと違うボックスを選択したとき, ボックスの選択をtrueにする
      setIsBoxChecked(() => ({
        isSelected: true,
        isFixed: bool,
      }));
    } else {
      // 選択されているボックスと同じボックスを選択したとき, ボックスの選択を前回の状態から反転させる
      setIsBoxChecked((prevState) => ({
        isSelected: !prevState.isSelected,
        isFixed: false, //クリアする
      }));
    }
  };

  // 該当項目がない状態の切り替え処理
  const toggleApplicable = () => {
    setIsNotApplicable((prevState) => !prevState);
    if (isFactorsChecked.some((value) => value)) {
      setIsFactorsChecked(isFactorsChecked.map(() => false));
    }
  };

  // 上部チェックボックスが選択されていない または 「直らない」が選択されている 場合にisNotApplicableをfalseにする
  useEffect(() => {
    if (!isBoxChecked.isSelected || !isBoxChecked.isFixed) {
      setIsNotApplicable(false);
    }
  }, [isBoxChecked]);

  useEffect(() => {
    if (isFactorsChecked.some((value) => value)) {
      setIsNotApplicable(false);
    }
    //チェックされた解消要因のラベルを保存する
    let resolvedFactors = [] as string[];
    // eslint-disable-next-line array-callback-return
    isFactorsChecked.map((item, index) => {
      if (item) {
        resolvedFactors.push(listData.inferenceFactor[index].issueFactor.label);
      }
    });
    setCheckResolvedFactorList(resolvedFactors);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFactorsChecked]);

  //アコーディオンメニューをレンダリングする関数
  const renderFeedbackAccordion = () => {
    return listData.inferenceFactor.map((inferenceFactor, index) => (
      <FeedbackAccordionList
        inferenceFactor={inferenceFactor}
        inferenceFactorNumber={index}
        isFactorsChecked={isFactorsChecked[index]}
        setIsFactorsChecked={setIsFactorsChecked}
        isBoxChecked={isBoxChecked}
        isNotApplicable={isNotApplicable}
        key={inferenceFactor.issueFactor.label}
      >
        <FeedbackAccordionList
          inferenceFactor={inferenceFactor}
          isBoxChecked={isBoxChecked}
          isNotApplicable={isNotApplicable}
        />
      </FeedbackAccordionList>
    ));
  };

  return (
    // スクロール不可に設定
    <div className="flex flex-col h-svh overflow-hidden">
      {/* header */}
      <GeneralCommonHeader
        title={t("フィードバック画面.フィードバック")} //フィードバック
        displayheaderIcon={DISPLAY_HEADER_ICON_BACK}
        onBtnClick={() => {
          navigate(-1);
        }}
      />
      {/* header以下 */}
      <div className="flex flex-col justify-between m-4 h-full overflow-hidden">
        {/* 入力済項目 */}
        <div className="mb-2">
          <div className="pb-1 flex gap-2">
            <div className="shrink-0 max-w-[146px]">
              {t("フィードバック画面.案件名")} :{/* 案件名： */}
            </div>
            <div className="flex-1 min-w-0 break-words">{subjectName}</div>
          </div>
          <div className="pb-1 flex gap-2">
            <div className="shrink-0">
              {t("フィードバック画面.型式")} :{/* 型式：  */}
            </div>
            <div className="flex-1 min-w-0 break-words">{model}</div>
          </div>
          <div className="pb-1 flex gap-2">
            <div className="shrink-0">
              {t("フィードバック画面.機番")} :{/* 機番：  */}
            </div>
            <div className="flex-1 min-w-0 break-words">{machineNumber}</div>
          </div>
          <div className="flex justify-around my-1">
            <div className="flex items-center">
              <p className="mx-1">
                {t("フィードバック画面.直った")}
                {/* 直った */}
              </p>
              <div
                className="px-3 cursor-pointer"
                onClick={() => handleFixCheck(true)}
              >
                {isBoxChecked.isSelected && isBoxChecked.isFixed ? (
                  <PiCheckSquare size={"1.25rem"} />
                ) : (
                  <PiSquare size={"1.25rem"} />
                )}
              </div>
            </div>
            <div className="flex items-center">
              <p className="mx-1">
                {t("フィードバック画面.直らない")}
                {/* 直らない */}
              </p>
              <div
                className="px-3 cursor-pointer"
                onClick={() => handleFixCheck(false)}
              >
                {isBoxChecked.isSelected && !isBoxChecked.isFixed ? (
                  <PiCheckSquare size={"1.25rem"} />
                ) : (
                  <PiSquare size={"1.25rem"} />
                )}
              </div>
            </div>
          </div>
          <p className="text-end pt-2">
            {t("フィードバック画面.原因に該当した項目を選択")}
            {/* 原因に該当した項目を選択 */}
          </p>
        </div>
        {/* フィードバック画面 */}
        <div className="h-full overflow-y-scroll overflow-x-hidden">
          {renderFeedbackAccordion()}
          <div className="flex justify-end items-center">
            <p className="mx-1">
              {t("フィードバック画面.上記項目に該当なし")}
              {/* 上記項目に該当なし */}
            </p>
            {isBoxChecked.isSelected && isBoxChecked.isFixed ? (
              isNotApplicable ? (
                <div className="px-3 cursor-pointer" onClick={toggleApplicable}>
                  <PiCheckSquare size={"1.25rem"} />
                </div>
              ) : (
                <div className="px-3 cursor-pointer" onClick={toggleApplicable}>
                  <PiSquare size={"1.25rem"} />
                </div>
              )
            ) : (
              // <div className="flex justify-end w-8">
              <div className="px-3">
                <PiSquareDuotone size={"1.25rem"} />
              </div>
            )}
          </div>
          {/* ボタン */}
          <div className="flex justify-around">
            <FeeedbackTempSaveButton
              disabled={false}
              userId={userId}
              searchHistoryId={searchHistoryId}
              resolved={
                isBoxChecked.isSelected
                  ? isBoxChecked.isFixed
                    ? "0"
                    : "1"
                  : ""
              }
              resolvedFactor={checkResolvedFactorList}
              notApplicableFlag={isNotApplicable ? "0" : ""}
            />
            <FeeedbackSaveButton
              disabled={
                !(
                  isFactorsChecked.some((value) => value) ||
                  isNotApplicable ||
                  (isBoxChecked.isSelected && !isBoxChecked.isFixed)
                )
              }
              userId={userId}
              searchHistoryId={searchHistoryId}
              resolved={
                isBoxChecked.isSelected
                  ? isBoxChecked.isFixed
                    ? "0"
                    : "1"
                  : ""
              }
              resolvedFactor={checkResolvedFactorList}
              notApplicableFlag={isNotApplicable ? "0" : ""}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
